import React from 'react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { RadioOrSelectOption, RootState } from '../../../../types/common.types';
import { Member } from '../../../../types/entity.types';

import { useApprovalApi } from '../../../../api/approval/useApprovalApi';
import { useMemberApi } from '../../../../api/member/useMemberApi';

import TribeApprovals from '../../../../components/screens/programme/tribe/tribe-approvals/TribeApprovals';

const TribeApprovalsContainer = () => {
  const [membersToDisplay, setMembersToDisplay] = useState<Member[]>([]);
  const [filter, setFilter] = useState<RadioOrSelectOption>({
    label: 'All Awaiting Approval',
    value: 'all:pending approval'
  });

  const { approvalMembers, posts } = useSelector(({ tribe }: RootState) => tribe);
  const { members } = useSelector(({ member }: RootState) => member);

  const { status, programmeExternalReference } = useParams();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';

  const { viewTribeMembers } = useMemberApi();

  const {
    viewApprovals,
    viewApprovalMembers,
    viewApprovalsForUser: viewApprovalsForUserRequest,
    updateAllPostStatus: updateAllPostStatusRequest,
    updateAllCommentStatus: updateAllCommentStatusRequest
  } = useApprovalApi();

  useEffect(() => {
    if (!programmeExternalReference) return;

    viewTribeMembers({ programmeExternalReference });
  }, [programmeExternalReference, status, viewTribeMembers]);

  useEffect(() => {
    const [type, status] = filter.value.split(':');
    viewApprovals({ programmeExternalReference: programmeExternalReference!, type: type!, status: status! });
    viewApprovalMembers({ programmeExternalReference: programmeExternalReference! });
  }, [programmeExternalReference, status, filter, viewApprovals, viewApprovalMembers]);

  useEffect(() => {
    if (approvalMembers && members) {
      const mappedMembers = approvalMembers.map((approvalMember) => {
        const { memberExternalReference } = approvalMember;
        const member = members.find((member) => member.memberExternalReference === memberExternalReference);
        return {
          ...approvalMember,
          profilePicture: member?.profilePicture
        };
      });

      setMembersToDisplay(mappedMembers);
    }
  }, [approvalMembers, members]);

  const viewApprovalsForUser = (systemUserExternalReference: string, onSuccess: () => void) => {
    viewApprovalsForUserRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        systemUserExternalReference
      },
      onSuccess
    );
  };

  const updateAllPosts = (systemUserExternalReference: string, status: string, onSuccess: () => void) => {
    updateAllPostStatusRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        systemUserExternalReference,
        status
      },
      () => {
        onSuccess();
        const [type, status] = filter.value.split(':');
        viewApprovals({ programmeExternalReference: programmeExternalReference!, type: type!, status: status! });
        viewApprovalMembers({ programmeExternalReference: programmeExternalReference! });
      }
    );
  };

  const updateAllComments = (systemUserExternalReference: string, status: string, onSuccess: () => void) => {
    updateAllCommentStatusRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        systemUserExternalReference,
        status
      },
      () => {
        onSuccess();
        const [type, status] = filter.value.split(':');
        viewApprovals({ programmeExternalReference: programmeExternalReference!, type: type!, status: status! });
        viewApprovalMembers({ programmeExternalReference: programmeExternalReference! });
      }
    );
  };

  const navigateBack = () => {
    navigate(`/programmes/${status}/${programmeExternalReference}/tribe?hasSubProgrammes=${hasSubProgrammes}`);
  };

  return (
    <TribeApprovals
      status={status as string}
      posts={posts}
      members={membersToDisplay}
      filter={filter}
      setFilter={setFilter}
      viewApprovalsForUser={viewApprovalsForUser}
      updateAllPosts={updateAllPosts}
      updateAllComments={updateAllComments}
      navigateBack={navigateBack}
    />
  );
};

export default TribeApprovalsContainer;
